import React, { useEffect } from 'react'
import { useComments } from '../../hooks/useComments'
import { useAuth } from '../../hooks/useAuth'
import { CommentList } from './CommentList'
import { CommentForm } from './CommentForm'
import { LoginButton } from '../auth/LoginButton'

interface CommentSectionProps {
  slug: string
}

export const CommentSection: React.FC<CommentSectionProps> = ({ slug }) => {
  const { isLoggedIn } = useAuth()
  const {
    comments,
    loading,
    submitting,
    error,
    load,
    addComment,
    likeComment,
    deleteComment,
  } = useComments(slug)

  useEffect(() => {
    load()
  }, [load])

  const handleTopLevelComment = async (body: string): Promise<boolean> => {
    return addComment(body)
  }

  const handleReply = async (body: string, parentId: string): Promise<boolean> => {
    return addComment(body, parentId)
  }

  return (
    <>
      <style>{`
        .comment-section {
          margin-top: 48px;
          padding-top: 32px;
          border-top: 0.5px solid var(--border-color-solid);
        }
        .comment-section-heading {
          font-family: 'JetBrains Mono', 'Courier New', monospace;
          font-size: 1rem;
          font-weight: 700;
          color: var(--accent-blue);
          margin-bottom: 24px;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .comment-section-count {
          font-size: 0.75rem;
          color: var(--text-muted);
          font-weight: 400;
        }
        .comment-auth-prompt {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px 16px;
          background-color: var(--bg-tertiary);
          border: 0.5px solid var(--border-color-solid);
          border-radius: var(--radius-md, 8px);
          margin-bottom: 24px;
        }
        .comment-auth-prompt-text {
          font-family: 'JetBrains Mono', 'Courier New', monospace;
          font-size: 0.78rem;
          color: var(--text-secondary);
        }
        .comment-new-form {
          margin-bottom: 32px;
          padding-bottom: 24px;
          border-bottom: 0.5px solid var(--border-color-solid);
        }
        .comment-new-form-label {
          font-family: 'JetBrains Mono', 'Courier New', monospace;
          font-size: 0.75rem;
          color: var(--text-muted);
          margin-bottom: 8px;
          display: block;
        }
      `}</style>
      <section className="comment-section" aria-label="Comments">
        <h2 className="comment-section-heading">
          // comments
          {comments.length > 0 && (
            <span className="comment-section-count">({comments.length})</span>
          )}
        </h2>

        {isLoggedIn ? (
          <div className="comment-new-form">
            <span className="comment-new-form-label">// leave a comment</span>
            <CommentForm
              onSubmit={handleTopLevelComment}
              loading={submitting}
              submitLabel="[post comment]"
            />
          </div>
        ) : (
          <div className="comment-auth-prompt">
            <span className="comment-auth-prompt-text">Sign in to leave a comment</span>
            <LoginButton />
          </div>
        )}

        {error && !loading && (
          <div
            style={{
              marginBottom: '16px',
              padding: '10px 14px',
              background: 'rgba(248,113,113,0.06)',
              border: '0.5px solid rgba(248,113,113,0.2)',
              borderRadius: 'var(--radius-sm)',
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: '0.8rem',
              color: 'var(--accent-red)',
            }}
          >
            {error}
          </div>
        )}

        <CommentList
          comments={comments}
          loading={loading}
          error={null}
          onLike={likeComment}
          onDelete={deleteComment}
          onReply={handleReply}
        />
      </section>
    </>
  )
}
